import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import { useMemo } from 'react';
import type { VehicleAlert, VehicleAlertLink } from './types';
import styled from '@emotion/styled';
import type { AlertColor, ButtonProps } from '@mui/material';
import { Button as MuiButton, Typography } from '@mui/material';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { SharedProps, VariantType } from 'notistack';
import { Trans, useTranslation } from 'react-i18next';
import { useConcatWord } from '@utils/i18n';
import { useDocumentLink } from '@data/i18n';
import type { VehicleAlertType } from './types';
import type { LinkProps } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useRootPath } from '@data/fms/environment/hooks';
import {
  environmentOptionAtom,
  remoteMonitoringSettingAtom,
} from '@data/fms/environment/states';
import { OpenInNew } from '@mui/icons-material';
import { atom, useAtomValue } from 'jotai';
import { atomWithReset } from 'jotai/utils';
import { getDriveLink } from '@components/common/DriveNotification/utils';

export const vehicleAlertListAtom = atomWithReset<VehicleAlert[]>([]);

export const confirmedCountAtom = atom(0);

export const getVehicleAlertLevel = (type: VehicleAlertType): VariantType => {
  if (
    type === 'vehicleError' ||
    type === 'agentError' ||
    type === 'mapUpdateFailure' ||
    type === 'agentUpdateFailure' ||
    type === 'firmwareUpdateFailure' ||
    type === 'individualParameterTampered' ||
    type === 'diagnosticsNotice' ||
    type === 'cameraQualityDeterioration' ||
    type === 'audioQualityDeterioration' ||
    type === 'mrm' ||
    type === 'rtc' ||
    type === 'heartbeatRejected' ||
    type === 'heartbeatDisconnected' ||
    type === 'softwareUpdateAbortFailure'
  ) {
    return 'error';
  }
  if (
    type === 'stuckAlarm' ||
    type === 'batteryAlarm' ||
    type === 'agentWarning' ||
    type === 'individualParameterSuggested' ||
    type === 'telemetryArrivalErrorAlarm'
  ) {
    return 'warning';
  }
  if (type === 'disconnect') {
    return 'info';
  }
  if (type === 'receiveCall' || type === 'hangupCall') {
    return type;
  }
  return 'info';
};

export const useVehicleAlertContent = (alert: VehicleAlert) => {
  const { t } = useTranslation();
  const { concat } = useConcatWord();
  const rootPath = useRootPath();
  const environmentOption = useAtomValue(environmentOptionAtom);
  const { createDocumentLink } = useDocumentLink();
  const remoteMonitoringSetting = useAtomValue(remoteMonitoringSettingAtom);

  /**
   * Level
   */
  const level = useMemo(
    (): SharedProps['variant'] | AlertColor => getVehicleAlertLevel(alert.type),
    [alert.type],
  );

  /**
   * Title
   */
  const title = useMemo(() => {
    // 車両エラー
    if (alert.type === 'vehicleError') {
      return t('vehicle.error');
    }
    // Agentエラー
    if (alert.type === 'agentError') {
      return concat(['Agent', t('vehicle.error')]);
    }
    // Agentワーニング
    if (alert.type === 'agentWarning') {
      return concat(['Agent', t('vehicle.warning')]);
    }
    // 立ち往生
    if (alert.type === 'stuckAlarm') {
      return t('vehicle.stuck');
    }
    // 未接続
    if (alert.type === 'disconnect') {
      return t('vehicle.telemetry.status.disconnected');
    }
    // バッテリー残量
    if (alert.type === 'batteryAlarm') {
      return concat([t('vehicle.telemetry.battery'), t('common.general.low')]);
    }
    // ネットワーク警告
    if (alert.type === 'telemetryArrivalErrorAlarm') {
      return t('vehicle.telemetry.telemetry_arrival_error');
    }
    // 更新失敗
    if (
      alert.type === 'mapUpdateFailure' ||
      alert.type === 'agentUpdateFailure' ||
      alert.type === 'firmwareUpdateFailure'
    ) {
      return t('notification.update_failure.title', { type: alert.type });
    }
    if (
      alert.type === 'individualParameterImported' ||
      alert.type === 'individualParameterSuggested'
    ) {
      return t('notification.individual_parameter.suggestion.title');
    }
    // 車両固有パラメータ改変あり
    if (alert.type === 'individualParameterTampered') {
      return t('notification.individual_parameter.tampered.title');
    }
    // カスタム通知
    if (alert.type === 'diagnosticsNotice') {
      return t('notification.custom_notification.health_check.title');
    }
    // 電話着信
    if (alert.type === 'receiveCall') {
      return t('notification.drive.call.start.title');
    }
    // 着信終了
    if (alert.type === 'hangupCall') {
      return t('notification.drive.call.end.title');
    }
    // カメラ品質アラート
    if (alert.type === 'cameraQualityDeterioration') {
      return t('notification.drive.camera_quality_deterioration.title');
    }
    // オーディオ品質アラート
    if (alert.type === 'audioQualityDeterioration') {
      return t('notification.drive.audio_quality_deterioration.title');
    }
    // MRM
    if (alert.type === 'mrm') {
      return t('notification.drive.mrm.title');
    }
    // RTC
    if (alert.type === 'rtc') {
      return t('notification.drive.rtc.title');
    }
    // Heartbeat 関連
    if (alert.type === 'heartbeatRejected') {
      return t('notification.drive.heartbeat.rejected.title');
    }
    if (alert.type === 'heartbeatDisconnected') {
      return t('notification.drive.heartbeat.disconnected.title');
    }
    // ソフトウェアアップデート中止
    if (alert.type === 'softwareUpdateAbortFailure') {
      return t('notification.software_update_abort.title');
    }
    return '';
  }, [alert.type, t, concat]);

  /**
   * Vehicle Name
   */
  const vehicleName = useMemo(() => {
    return (
      alert.vehicle &&
      `${t('vehicle.vehicle_name')}: ${alert.vehicle.vehicle_name}`
    );
  }, [alert, t]);

  /**
   * Message
   */
  const message = useMemo(() => {
    // Battery
    if (alert.type === 'batteryAlarm') {
      return (
        <AlertContentTypography>
          {t('common.alert.battery', {
            count: alert.vehicle.telemetry.battery,
          })}
        </AlertContentTypography>
      );
    }
    // Agentエラー
    if (
      alert.type === 'agentError' &&
      !isNullOrUndefined(alert.vehicle) &&
      !isNullOrUndefined(alert.agentErrorMessage)
    ) {
      return (
        <AlertContentTypography sx={{ wordBreak: 'break-all' }}>
          {alert.agentErrorMessage}
        </AlertContentTypography>
      );
    }
    // Agentワーニング
    if (
      alert.type === 'agentWarning' &&
      !isNullOrUndefined(alert.vehicle) &&
      !isNullOrUndefined(alert.agentWarningMessage)
    ) {
      return (
        <AlertContentTypography>
          {alert.agentWarningMessage}
        </AlertContentTypography>
      );
    }
    // 更新失敗
    if (
      alert.type === 'mapUpdateFailure' ||
      alert.type === 'agentUpdateFailure' ||
      alert.type === 'firmwareUpdateFailure'
    ) {
      return (
        <AlertContentTypography sx={{ whiteSpace: 'pre-line' }}>
          {t('notification.update_failure.message', { type: alert.type })}
        </AlertContentTypography>
      );
    }
    // 車両固有パラメータ候補値
    if (alert.type === 'individualParameterSuggested') {
      return (
        <AlertContentTypography>
          {t('notification.individual_parameter.suggestion.message.suggested')}
        </AlertContentTypography>
      );
    }
    if (alert.type === 'individualParameterImported') {
      return (
        <AlertContentTypography>
          {t('notification.individual_parameter.suggestion.message.imported')}
        </AlertContentTypography>
      );
    }
    // 車両固有パラメータ改変あり
    if (alert.type === 'individualParameterTampered') {
      return (
        <AlertContentTypography>
          {t('notification.individual_parameter.tampered.message')}
        </AlertContentTypography>
      );
    }
    if (alert.type === 'telemetryArrivalErrorAlarm') {
      return (
        <AlertContentTypography>
          <Trans
            i18nKey="notification.telemetry_arrival_error"
            components={{
              docs: (
                <a
                  href={createDocumentLink({
                    ja: '/user-manuals/fleet-management-system/webauto-agent/troubleshooting#%E3%83%8D%E3%83%83%E3%83%88%E3%83%AF%E3%83%BC%E3%82%AF',
                    'en-US':
                      '/user-manuals/fleet-management-system/webauto-agent/troubleshooting#network',
                  })}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  link
                </a>
              ),
            }}
          />
        </AlertContentTypography>
      );
    }
    if (alert.type === 'receiveCall') {
      return (
        <AlertContentTypography>
          {t('notification.drive.call.start.message')}
        </AlertContentTypography>
      );
    }
    if (alert.type === 'hangupCall') {
      return (
        <AlertContentTypography>
          {t('notification.drive.call.end.message')}
        </AlertContentTypography>
      );
    }
    // カメラ品質アラート
    if (alert.type === 'cameraQualityDeterioration') {
      return (
        <AlertContentTypography>
          {t('notification.drive.camera_quality_deterioration.message')}
        </AlertContentTypography>
      );
    }
    // オーディオ品質アラート
    if (alert.type === 'audioQualityDeterioration') {
      return (
        <AlertContentTypography>
          {t('notification.drive.audio_quality_deterioration.message')}
        </AlertContentTypography>
      );
    }
    // MRM
    if (alert.type === 'mrm') {
      return (
        <AlertContentTypography>
          {t('notification.drive.mrm.message')}
        </AlertContentTypography>
      );
    }
    // RTC
    if (alert.type === 'rtc') {
      return (
        <AlertContentTypography>
          {t('notification.drive.rtc.message')}
        </AlertContentTypography>
      );
    }
    // Heartbeat 関連
    if (alert.type === 'heartbeatRejected') {
      return (
        <AlertContentTypography>
          {t('notification.drive.heartbeat.rejected.message')}
        </AlertContentTypography>
      );
    }
    if (alert.type === 'heartbeatDisconnected') {
      return (
        <AlertContentTypography>
          {t('notification.drive.heartbeat.disconnected.message')}
        </AlertContentTypography>
      );
    }
    if (alert.type === 'softwareUpdateAbortFailure') {
      const abortStatusReason =
        alert.vehicle.firmware_deployment_abort_status_reason;
      return (
        <AlertContentTypography sx={{ whiteSpace: 'pre-line' }}>
          {t('notification.software_update_abort.message.failure')}
          {abortStatusReason && (
            <>
              {'\n'}
              {abortStatusReason}
            </>
          )}
        </AlertContentTypography>
      );
    }
    return null;
  }, [alert, t, createDocumentLink]);

  /**
   * Link Button
   */
  const linkButton = useMemo(() => {
    if (
      alert.type === 'disconnect' ||
      alert.type === 'softwareUpdateAbortFailure' ||
      (alert.type === 'telemetryArrivalErrorAlarm' &&
        !remoteMonitoringSetting?.value.notification) ||
      isNullOrUndefined(alert.vehicle)
    ) {
      return null;
    }

    const getLink = (): VehicleAlertLink => {
      if (alert.type === 'vehicleError') {
        // エラー一覧
        return {
          label: t('page.error_detail'),
          to: `${rootPath}error-detail/${alert.vehicle.vehicle_id}/?condition=${environmentOption?.error_alert_condition}`,
        };
      } else if (
        alert.type === 'mapUpdateFailure' ||
        alert.type === 'agentUpdateFailure' ||
        alert.type === 'firmwareUpdateFailure'
      ) {
        // 車両一覧
        return {
          label: t('page.vehicle_management'),
          to: `${rootPath}vehicle-management/`,
        };
      } else if (
        alert.type === 'individualParameterSuggested' ||
        alert.type === 'individualParameterImported'
      ) {
        // 車両状態確認 -> 車両固有パラメータ
        return {
          label: t('vehicle_condition.list.individual_parameter'),
          to: `${rootPath}vehicle-condition/parameter/${alert.vehicle.vehicle_id}/?type=suggestion`,
        };
      } else if (alert.type === 'individualParameterTampered') {
        // 車両状態確認 -> 車両固有パラメータ
        return {
          label: t('vehicle_condition.list.individual_parameter'),
          to: `${rootPath}vehicle-condition/parameter/${alert.vehicle.vehicle_id}/`,
        };
      } else if (alert.type === 'agentError') {
        // Agent エラー -> ドキュメントへのリンク
        return {
          label: t('notification.vehicle_agent_error.document_link'),
          to: createDocumentLink(
            '/user-manuals/fleet-management-system/fms-console/error-notification/',
          ),
          isExternal: true,
        };
      } else if (alert.type === 'diagnosticsNotice') {
        // カスタム通知 -> 車両詳細（ヘルスチェックモーダル表示）
        return {
          label: t('page.vehicle_detail'),
          to: `${rootPath}vehicle/${alert.vehicle.vehicle_id}/?health_check=true`,
        };
      } else if (alert.type === 'telemetryArrivalErrorAlarm') {
        /**
         * テレメトリー到着エラー -> Drive へのリンク
         * NOTE: remoteMonitoringSetting.value.notification === true の場合のみ
         */
        return {
          label: t('notification.drive.link'),
          to: getDriveLink(alert.vehicle),
          isExternal: true,
        };
      } else if (
        alert.type === 'receiveCall' ||
        alert.type === 'hangupCall' ||
        alert.type === 'cameraQualityDeterioration' ||
        alert.type === 'audioQualityDeterioration' ||
        alert.type === 'mrm' ||
        alert.type === 'rtc' ||
        alert.type === 'heartbeatRejected' ||
        alert.type === 'heartbeatDisconnected'
      ) {
        // Drive 関連の通知 -> Drive へのリンク
        return {
          label: t('notification.drive.link'),
          to: getDriveLink(alert.vehicle),
          isExternal: true,
        };
      } else {
        // 車両詳細
        return {
          label: t('page.vehicle_detail'),
          to: `${rootPath}vehicle/${alert.vehicle.vehicle_id}/`,
        };
      }
    };

    const link = getLink();
    const color =
      level === 'receiveCall'
        ? 'warning'
        : level === 'hangupCall'
        ? 'info'
        : (level as 'success' | 'warning' | 'info');

    if (link.isExternal) {
      // 外部リンク
      return (
        <Button
          href={link.to}
          size="small"
          variant="outlined"
          color={color}
          target="_blank"
          rel="noreferrer noopener"
          endIcon={<OpenInNew />}
          data-testid="snackbar-link-button"
        >
          {link.label}
        </Button>
      );
    }

    // アプリ内リンク
    return (
      <LinkButton
        component={Link}
        to={link.to}
        size="small"
        variant="outlined"
        color={color}
        data-testid="snackbar-link-button"
      >
        {link.label}
      </LinkButton>
    );
  }, [
    alert,
    t,
    rootPath,
    environmentOption,
    level,
    createDocumentLink,
    remoteMonitoringSetting?.value.notification,
  ]);

  return {
    level,
    title,
    vehicleName,
    message,
    linkButton,
  };
};

const Button = styled(MuiButton)<ButtonProps & { target?: string }>`
  margin-top: ${({ theme }) => theme.spacing(2)};
  background-color: white;
  font-size: 12px;
  padding: 2px 9px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.8);
  }
`;

const LinkButton = styled(Button)<
  {
    component: ForwardRefExoticComponent<
      LinkProps & RefAttributes<HTMLAnchorElement>
    >;
    to: string;
  } & ButtonProps
>``;

const AlertContentTypography = styled(Typography)`
  margin-top: ${({ theme }) => theme.spacing(2)};

  a {
    color: inherit;
    text-decoration: underline;
    font-weight: bold;
  }
`;
